import { Box, Circle, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessPersonalResultsQuery,
  PerformanceScoreConfigurationFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  orderStrategies,
  strategyColor,
  strategyTranslation,
} from 'modules/performance_process/lib/reviewStrategy'

type PerformanceReviewStrategyEmployeeScore = NonNullable<
  NonNullable<
    PerformanceProcessPersonalResultsQuery['performance']['reviewProcessTarget']
  >['reviewStrategyEmployeeScores'][number]
>

type Props = {
  strategyScores: Array<
    Pick<PerformanceReviewStrategyEmployeeScore, 'id' | 'reviewerStrategy' | 'scaleMax' | 'score'>
  >
  scoreConfiguration: PerformanceScoreConfigurationFragment
  canSeeWeights: boolean
}

export const StrategyScores = ({ strategyScores, scoreConfiguration, canSeeWeights }: Props) => {
  const visibleStrategies = orderStrategies(
    (scoreConfiguration?.strategyConfigurations || [])
      .filter((config) => config.producesScore)
      .map((config) => config.reviewerStrategy)
  )

  const scores = visibleStrategies.map((strategy) => {
    const score = strategyScores.find(
      (strategyScore) => strategyScore.reviewerStrategy === strategy
    )
    const strategyConfig = scoreConfiguration?.strategyConfigurations?.find(
      (config) => config.reviewerStrategy === strategy
    )

    return {
      reviewerStrategy: strategy,
      score: score?.score,
      scaleMax: score?.scaleMax,
      weight: strategyConfig?.weight,
    }
  })

  return (
    <Box
      role="table"
      flexDirection="column"
      gap="s4"
      aria-label="Performance review scores by strategy"
    >
      <Box role="rowgroup">
        <Box role="row" flexDirection="row" justifyContent="spaceBetween" gap="s6">
          <Box role="columnheader">
            <Text size="200" color="grey800">
              {i18n.t('performance_process.targets.insights.performance_score_detail.strategy')}
            </Text>
          </Box>
          <Box role="columnheader" textAlign="right">
            <Text size="200" color="grey800">
              {i18n.t('performance_process.targets.insights.performance_score_detail.score')}
            </Text>
          </Box>
        </Box>
      </Box>
      <Box role="rowgroup" flexDirection="column" gap="s4">
        {scores.map(({ reviewerStrategy, score, scaleMax, weight }) => (
          <Box
            key={reviewerStrategy}
            flexDirection="row"
            gap="s6"
            justifyContent="spaceBetween"
            width="full"
          >
            <Box role="cell" flexDirection="row" gap="s8" alignItems="center">
              <Box alignSelf="center">
                <Circle color={strategyColor(reviewerStrategy)[1]} size={10} />
              </Box>
              <Text.Normal size="200" color="grey800">
                {strategyTranslation(reviewerStrategy)}
                {canSeeWeights && weight != null && (
                  <Text.Normal size="200">&nbsp;- {i18n.formatNumber(weight)}%</Text.Normal>
                )}
              </Text.Normal>
            </Box>

            <Box role="cell" flexDirection="row" alignItems="baseline">
              <Text size="200" weight="medium" color="grey1000">
                {score ? i18n.formatNumber(score) : '-'}
              </Text>
              {scaleMax && (
                <Text size="200" weight="medium" color="grey1000">
                  /{i18n.formatNumber(scaleMax)}
                </Text>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
